import {
  isUndefined,
  maxBy,
  minBy,
  negate,
  orderBy,
  sortBy,
  uniq,
} from 'lodash';
import { DateTime } from 'luxon';
import { getNumber, valueGetters } from './utils';

export type Unit = string;
export type Category = string;
export type VictoryDatum = {
  x: number;
  y: number;
  asString: string;
  unit: string;
  category: Category;
  delta: number | null;
};

const isNotUndefined = negate(isUndefined);

class Measurement {
  constructor(
    public dateTime: DateTime,
    public value: number,
    public valueAsString: string,
    public code: fhir.CodeableConcept,
    public unit: string,
  ) {}

  get category(): Category {
    return (
      this.code.text ?? this.code.coding?.find(c => c.display)?.display ?? ''
    );
  }

  static *fromObservation(
    observation: fhir.Observation,
  ): Generator<Measurement, void, unknown> {
    const dateTime = Measurement.getDateTime(observation);
    if (!dateTime) return;
    for (const component of [observation, ...(observation.component ?? [])]) {
      const measurement = Measurement.fromObservationLike(component, dateTime);
      if (measurement) yield measurement;
    }
  }

  static fromObservationLike(
    observationLike: fhir.Observation | fhir.ObservationComponent,
    dateTime: DateTime,
  ): Measurement | null {
    const measurementValue =
      valueGetters.getValueQuantity(observationLike.valueQuantity) ??
      valueGetters.getValueString(observationLike.valueString);
    if (!measurementValue) return null;
    return new Measurement(
      dateTime,
      measurementValue.value,
      measurementValue.valueAsString,
      observationLike.code,
      measurementValue.unit ?? '',
    );
  }

  static getDateTime(observation: fhir.Observation): DateTime | null {
    const dateTime = DateTime.fromISO(
      observation.effectiveDateTime ??
        observation.effectivePeriod?.end ??
        observation.effectivePeriod?.start ??
        '',
    );
    return dateTime.isValid ? dateTime : null;
  }
}

export class MeasurementsByCategory {
  minValue: Measurement;
  maxValue: Measurement;

  constructor(
    public category: Category,
    public measurements: Measurement[] = [],
  ) {
    this.minValue = minBy(this.measurements, m => m.value)!;
    this.maxValue = maxBy(this.measurements, m => m.value)!;
  }

  addMeasurement(measurement: Measurement) {
    this.measurements.push(measurement);
    this.minValue = minBy(this.measurements, m => m.value)!;
    this.maxValue = maxBy(this.measurements, m => m.value)!;
  }
}

export class MeasurementsByUnit {
  minValue: Measurement;
  maxValue: Measurement;

  constructor(
    public unit: Unit,
    public byCategory: Record<Category, MeasurementsByCategory> = {},
  ) {
    this.minValue = minBy(
      Object.values(this.byCategory).map(c => c.minValue),
      m => m.value,
    )!;
    this.maxValue = maxBy(
      Object.values(this.byCategory).map(c => c.maxValue),
      m => m.value,
    )!;
  }

  addMeasurement(measurement: Measurement) {
    if (!this.byCategory[measurement.category]) {
      this.byCategory[measurement.category] = new MeasurementsByCategory(
        measurement.category,
      );
    }
    this.byCategory[measurement.category].addMeasurement(measurement);
    this.minValue = minBy(
      [this.minValue, measurement].filter(isNotUndefined),
      m => m.value,
    )!;
    this.maxValue = maxBy(
      [this.maxValue, measurement].filter(isNotUndefined),
      m => m.value,
    )!;
  }

  get byCategoryArray(): MeasurementsByCategory[] {
    return orderBy(
      Object.values(this.byCategory),
      [category => category.minValue.value],
      ['asc'],
    );
  }

  intoVictoryData(): { data: VictoryDatum[][]; ticks: (DateTime | null)[] } {
    const allDateTimes = sortBy(
      uniq(
        Object.values(this.byCategory)
          .flatMap(c => c.measurements)
          .map(m => m.dateTime.toMillis()),
      ),
    );

    const indexMap = new Map<number, number>();
    let index = 0;

    for (const time of allDateTimes) {
      indexMap.set(time, index);
      index++;
    }

    const data = this.byCategoryArray.map(category => {
      return category.measurements.map((measurement, index) => {
        const previousMeasurement = category.measurements[index - 1];
        const delta = previousMeasurement
          ? getNumber(measurement.value - previousMeasurement.value)
          : null;
        return {
          x: indexMap.get(measurement.dateTime.toMillis())!,
          y: measurement.value,
          asString: measurement.valueAsString,
          unit: measurement.unit,
          category: category.category,
          delta,
        };
      });
    });
    const ticks = allDateTimes.map(time => DateTime.fromMillis(time));

    return { data, ticks };
  }
}

export class Measurements {
  constructor(public byUnit: Record<Unit, MeasurementsByUnit> = {}) {}

  addMeasurement(measurement: Measurement) {
    if (!this.byUnit[measurement.unit]) {
      this.byUnit[measurement.unit] = new MeasurementsByUnit(measurement.unit);
    }
    this.byUnit[measurement.unit].addMeasurement(measurement);
  }

  get units(): Unit[] {
    return Object.keys(this.byUnit);
  }

  static fromObservations(observations: fhir.Observation[]): Measurements {
    const measurements = new Measurements();
    const sortedObservations = orderBy(
      observations,
      [obs => obs.effectiveDateTime ?? obs.effectivePeriod?.end, obs => obs.id],
      ['asc', 'asc'],
    );
    for (const observation of sortedObservations) {
      for (const measurement of Measurement.fromObservation(observation)) {
        measurements.addMeasurement(measurement);
      }
    }
    return measurements;
  }
}
